import React from 'react';

import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react';

import { TYPES } from '@/engine/ioc';
import { Box, Checkbox, Collapse, IconButton, LinearProgress, Stack, Tooltip } from '@mui/material';

import styled from 'styled-components';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import SpeedIcon from '@mui/icons-material/Speed';

import type { AppController } from '@/engine/app/App.controller';
import type { ICineloopControlController } from './interfaces';

const DEFAULT_DELTA = 10;
const DEFAULT_MAX_BPM = 100;
const DEFAULT_MIN_BPM = 10;

export const CineloopControl: React.FC<{
  disabled: boolean;
  isExpanded: boolean;
  isShrinkGraphHidden: boolean;
  isActive: boolean;
}> = observer((props) => {
  const appController = useInjection<AppController>(TYPES.AppController);
  const cineloopController = useInjection<ICineloopControlController>(TYPES.ICineloopControlController);

  const [playing, setPlaying] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const bPM: number =
    appController.cineloopCurrentBpm !== undefined
      ? parseFloat(
          (Math.round(appController.cineloopCurrentBpm / (DEFAULT_DELTA / 2)) * (DEFAULT_DELTA / 2)).toFixed(0)
        )
      : NaN;

  const [targetBpm, setTargetBpm] = React.useState<number>(bPM);

  const handlePlayingChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setPlaying(event.target.checked);
    await cineloopController.playCineloop(event.target.checked);
  };

  const handleSpeedChangeFaster = (delta: number): void => {
    if (isNaN(bPM)) return;
    const newValue = Math.max(DEFAULT_MIN_BPM, Math.min(bPM + delta, DEFAULT_MAX_BPM));
    setTargetBpm(newValue);
    appController.setCineloopCineloopTargetBpm(newValue);

    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  return (
    <CineloopContainer
      $isExpanded={props.isExpanded}
      $isShrinkGraphHidden={props.isShrinkGraphHidden}
      className="cineloop-container"
    >
      <Container>
        <Collapse
          in={playing}
          collapsedSize={0}
          orientation="horizontal"
          timeout={{ appear: 500, enter: 500, exit: 0 }}
        >
          <Box
            sx={{
              width: 160,
              margin: '0.5em 1.5em 0 0.8em'
            }}
          >
            <Stack spacing={1} direction="row" sx={{ mb: 1 }} alignItems="center">
              <Tooltip title={'Slower'} placement="top">
                <IconButton
                  color="secondary"
                  aria-label="slower"
                  onClick={(): void => handleSpeedChangeFaster(-DEFAULT_DELTA)}
                  disabled={targetBpm <= DEFAULT_MIN_BPM}
                  size="small"
                >
                  <SpeedIcon style={{ transform: 'scaleX(-1)' }} />
                </IconButton>
              </Tooltip>
              <SpeedContainer>
                <BPMContainer>
                  BPM: <BPMValue>{isNaN(bPM) ? '--' : bPM}</BPMValue>
                </BPMContainer>
                {loading && <LinearProgress style={{ marginBottom: '-0.15em', opacity: 0.4 }} />}
              </SpeedContainer>
              <Tooltip title={'Faster'} placement="top">
                <IconButton
                  sx={{ '&': { marginLeft: 0 } }}
                  color="secondary"
                  aria-label="faster"
                  onClick={(): void => handleSpeedChangeFaster(DEFAULT_DELTA)}
                  disabled={targetBpm >= DEFAULT_MAX_BPM}
                  size="small"
                >
                  <SpeedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Collapse>
        <Tooltip
          title={playing ? 'Stop cineloop' : 'Start cineloop'}
          hidden={!playing && props.isActive}
          placement="top"
        >
          <Checkbox
            sx={{ '& svg': { fontSize: '2.2rem' }, padding: '0.4rem' }}
            checked={playing}
            data-testid={'PlayCineloop'}
            size="medium"
            onChange={(e) => {
              void handlePlayingChange(e);
            }}
            icon={<PlayCircleIcon />}
            checkedIcon={<PauseCircleIcon />}
            disabled={props.disabled || (!playing && props.isActive)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Tooltip>
      </Container>
    </CineloopContainer>
  );
});

const CineloopContainer = styled.div<{ $isExpanded: boolean; $isShrinkGraphHidden: boolean }>`
  position: sticky;
  display: flex;
  z-index: 999;
  height: 0;
  justify-content: flex-end;
  align-items: flex-end;
  top: ${({ $isShrinkGraphHidden }): string => ($isShrinkGraphHidden ? 'unset' : '7.5em')};
  bottom: ${({ $isExpanded }): string => ($isExpanded ? '16em' : 'unset')};
`;

const SpeedContainer = styled.div`
  width: 4.5em;
  text-align: center;
  height: 2em;
  overflow: hidden;
`;
const BPMContainer = styled.div`
  height: 1.5em;
  line-height: 2em;
  margin-bottom: 0.2em;
`;

const BPMValue = styled.span`
  color: ${({ theme }): string => theme.palette.primary.main};
  font-weight: 600;
`;

const Container = styled.div`
  display: flex;
  border-radius: 1.45rem;
  background-color: ${({ theme }): string => theme.customPalette.panels.cineloop + 'FF'};
  color: ${({ theme }): string => theme.palette.secondary.light};
  height: 2.9em;
`;
